import { customerFacingEdgeName, customerFacingSiteName, customerMetricLabel } from '@aot-aiops/shared';
import { customerFacingEntityName } from './presentation';

export type CommandCategory =
  | 'Navigation'
  | 'Assets'
  | 'Sites'
  | 'Services'
  | 'Incidents'
  | 'Customers'
  | 'Reports'
  | 'Knowledge'
  | 'Actions';

export type CommandResult = {
  id: string;
  category: CommandCategory;
  label: string;
  detail: string;
  view: string;
  entityId?: string;
  question?: string;
};

export type CommandSources = {
  assets?: any[];
  sites?: any[];
  services?: any[];
  incidents?: any[];
  customers?: any[];
  reports?: any[];
  knowledge?: any[];
};

const navigationCommands: CommandResult[] = [
  {
    id: 'nav-overview',
    category: 'Navigation',
    label: 'Go to Overview',
    detail: 'Operational command center',
    view: 'Overview',
  },
  {
    id: 'nav-services',
    category: 'Navigation',
    label: 'Open Services',
    detail: 'Business health, impact, and SLOs',
    view: 'Services',
  },
  {
    id: 'nav-sites',
    category: 'Navigation',
    label: 'Open Sites',
    detail: 'Site health and activity',
    view: 'Sites',
  },
  {
    id: 'nav-assets',
    category: 'Navigation',
    label: 'Open Assets',
    detail: 'Infrastructure inventory',
    view: 'Assets',
  },
  {
    id: 'nav-incidents',
    category: 'Navigation',
    label: 'Open Incidents',
    detail: 'NOC investigation workspace',
    view: 'Incidents',
  },
  {
    id: 'nav-topology',
    category: 'Navigation',
    label: 'Open Topology',
    detail: 'Infrastructure relationships',
    view: 'Topology',
  },
  {
    id: 'nav-capacity',
    category: 'Navigation',
    label: 'Open Capacity',
    detail: 'Findings and forecasts',
    view: 'Capacity',
  },
  {
    id: 'nav-reports',
    category: 'Navigation',
    label: 'Open Reports',
    detail: 'Operational report library',
    view: 'Reports',
  },
  {
    id: 'nav-opsai',
    category: 'Navigation',
    label: 'Open OpsAI',
    detail: 'Grounded operational intelligence',
    view: 'OpsAI',
  },
  {
    id: 'nav-knowledge',
    category: 'Navigation',
    label: 'Open Knowledge',
    detail: 'Approved runbooks and guidance',
    view: 'Knowledge',
  },
  {
    id: 'nav-settings',
    category: 'Navigation',
    label: 'Open Settings',
    detail: 'Workspace administration and Edge agents',
    view: 'Settings',
  },
];

const actionCommands: CommandResult[] = [
  {
    id: 'action-overnight',
    category: 'Actions',
    label: 'What happened in the last 24 hours?',
    detail: 'Ask OpsAI using current tenant evidence',
    view: 'OpsAI',
    question: 'What happened in the last 24 hours?',
  },
  {
    id: 'action-maintenance',
    category: 'Actions',
    label: 'Create maintenance window',
    detail: 'Open authorized maintenance settings',
    view: 'Settings',
  },
  {
    id: 'action-critical',
    category: 'Actions',
    label: 'Review critical incidents',
    detail: 'Open the incident workspace',
    view: 'Incidents',
  },
];

function matches(query: string, ...values: unknown[]) {
  return values.some((value) =>
    String(value ?? '')
      .toLowerCase()
      .includes(query),
  );
}

export function buildCommandResults(
  queryValue: string,
  sources: CommandSources,
  isAotOperator: boolean,
  maxPerCategory = 5,
  maxTotal = 30,
) {
  const query = queryValue.trim().toLowerCase();
  const take = <T>(items: T[]) => items.slice(0, maxPerCategory);
  const include = (item: CommandResult) =>
    !query || matches(query, item.label, item.detail, item.category);
  const results: CommandResult[] = [];

  results.push(...take(navigationCommands.filter(include)));
  results.push(
    ...take(
      (sources.assets ?? [])
        .filter(
          (item) =>
            !query ||
            matches(
              query,
              item.displayName,
              item.name,
              item.hostname,
              item.assetType,
              item.managementIp,
            ),
        )
        .map((item) => ({
          id: `asset-${item.id}`,
          category: 'Assets' as const,
          label: customerFacingEntityName(item.displayName ?? item.name, item.source === 'demo'),
          detail: `${item.assetType ?? item.kind} · ${
            item.siteDisplayName ??
            customerFacingSiteName({ name: item.siteName ?? 'Current tenant' })
          }`,
          view: 'Assets',
          entityId: item.id,
        })),
    ),
  );
  results.push(
    ...take(
      (sources.services ?? [])
        .filter(
          (item) =>
            !query || matches(query, item.name, item.description, item.healthState, item.ownerTeam),
        )
        .map((item) => ({
          id: `service-${item.id}`,
          category: 'Services' as const,
          label: customerFacingEntityName(item.name),
          detail: `${item.healthState ?? 'unknown'} · ${item.criticality ?? 'unclassified'} criticality`,
          view: 'Services',
          entityId: item.id,
        })),
    ),
  );
  results.push(
    ...take(
      (sources.sites ?? [])
        .filter(
          (item) => !query || matches(query, customerFacingSiteName(item), item.name, item.status),
        )
        .map((item) => ({
          id: `site-${item.id}`,
          category: 'Sites' as const,
          label: customerFacingSiteName(item),
          detail: `${item.status ?? 'active'} site`,
          view: 'Sites',
          entityId: item.id,
        })),
    ),
  );
  results.push(
    ...take(
      (sources.incidents ?? [])
        .filter(
          (item) => !query || matches(query, item.title, item.type, item.severity, item.status),
        )
        .map((item) => ({
          id: `incident-${item.id}`,
          category: 'Incidents' as const,
          label: customerFacingEntityName(item.title),
          detail: `${item.severity} · ${item.status}`,
          view: 'Incidents',
          entityId: item.id,
        })),
    ),
  );
  if (isAotOperator) {
    results.push(
      ...take(
        (sources.customers ?? [])
          .filter(
            (item) => !query || matches(query, item.displayName, item.name, item.status, item.plan),
          )
          .map((item) => ({
            id: `customer-${item.id}`,
            category: 'Customers' as const,
            label: customerFacingEntityName(item.displayName ?? item.name, Boolean(item.demo)),
            detail: `${item.status} · ${item.plan ?? 'Unassigned plan'}`,
            view: 'AOT Operations',
            entityId: item.id,
          })),
      ),
    );
  }
  results.push(
    ...take(
      (sources.reports ?? [])
        .filter((item) => !query || matches(query, item.reportType, item.status))
        .map((item) => ({
          id: `report-${item.id}`,
          category: 'Reports' as const,
          label: `${item.reportType} report`,
          detail: `${item.status} · ${item.generatedAt ?? item.periodEnd ?? ''}`,
          view: 'Reports',
        })),
    ),
  );
  results.push(
    ...take(
      (sources.knowledge ?? [])
        .filter((item) => !query || matches(query, item.title, item.type, item.source))
        .map((item) => ({
          id: `knowledge-${item.id}`,
          category: 'Knowledge' as const,
          label: item.title,
          detail: `${item.type} · ${item.source}`,
          view: 'Knowledge',
        })),
    ),
  );
  results.push(...take(actionCommands.filter(include)));
  return results.slice(0, maxTotal);
}

export function nextCommandIndex(current: number, key: string, length: number) {
  if (!length) return -1;
  if (key === 'ArrowDown') return current < 0 ? 0 : (current + 1) % length;
  if (key === 'ArrowUp') return current <= 0 ? length - 1 : current - 1;
  if (key === 'Home') return 0;
  if (key === 'End') return length - 1;
  return current;
}

export type TopologyNodeModel = { asset: any; children: TopologyNodeModel[] };

export function buildTopology(assets: any[], siteId?: string): TopologyNodeModel[] {
  const scoped = assets.filter((asset) => !siteId || asset.siteId === siteId);
  const byId = new Map(scoped.map((asset) => [asset.id, asset]));
  const children = new Map<string, any[]>();
  for (const asset of scoped) {
    if (asset.parentAssetId && byId.has(asset.parentAssetId)) {
      const list = children.get(asset.parentAssetId) ?? [];
      list.push(asset);
      children.set(asset.parentAssetId, list);
    }
  }
  const roots = scoped.filter((asset) => !asset.parentAssetId || !byId.has(asset.parentAssetId));
  const toNode = (asset: any, path: Set<string>): TopologyNodeModel => {
    if (path.has(asset.id)) return { asset, children: [] };
    const nextPath = new Set(path).add(asset.id);
    return {
      asset,
      children: (children.get(asset.id) ?? [])
        .sort((a, b) =>
          String(a.displayName ?? a.name).localeCompare(String(b.displayName ?? b.name)),
        )
        .map((child) => toNode(child, nextPath)),
    };
  };
  return roots
    .sort((a, b) => String(a.displayName ?? a.name).localeCompare(String(b.displayName ?? b.name)))
    .map((asset) => toNode(asset, new Set()));
}

export function relationshipPath(assets: any[], assetId: string) {
  const byId = new Map(assets.map((asset) => [asset.id, asset]));
  const result: any[] = [];
  const seen = new Set<string>();
  let current = byId.get(assetId);
  while (current && result.length < 12 && !seen.has(current.id)) {
    result.unshift(current);
    seen.add(current.id);
    current = current.parentAssetId ? byId.get(current.parentAssetId) : undefined;
  }
  return result;
}

export type TimelineCategory =
  'Incidents' | 'Maintenance' | 'Agents' | 'Capacity' | 'Configuration';
export type OperationalEvent = {
  id: string;
  category: TimelineCategory;
  title: string;
  detail: string;
  occurredAt: string;
  status: string;
  siteId?: string;
  assetId?: string;
  target?: 'incident' | 'asset' | 'site' | 'edge';
  targetId?: string;
};

export function buildOperationalEvents(input: {
  incidents?: any[];
  maintenance?: any[];
  agents?: any[];
  findings?: any[];
}): OperationalEvent[] {
  const events: OperationalEvent[] = [];
  for (const item of input.incidents ?? []) {
    events.push({
      id: `incident-open-${item.id}`,
      category: 'Incidents',
      title: 'Incident detected',
      detail: customerFacingEntityName(item.title),
      occurredAt: item.openedAt,
      status: item.severity,
      siteId: item.siteId,
      target: 'incident',
      targetId: item.id,
    });
    if (item.recoveredAt)
      events.push({
        id: `incident-recovered-${item.id}`,
        category: 'Incidents',
        title: 'Incident recovered',
        detail: customerFacingEntityName(item.title),
        occurredAt: item.recoveredAt,
        status: 'recovered',
        siteId: item.siteId,
        target: 'incident',
        targetId: item.id,
      });
  }
  for (const item of input.maintenance ?? []) {
    events.push({
      id: `maintenance-start-${item.id}`,
      category: 'Maintenance',
      title: 'Maintenance window started',
      detail: item.reason,
      occurredAt: item.startsAt,
      status: 'maintenance',
      siteId: item.siteId,
      assetId: item.assetId,
      target: item.assetId ? 'asset' : 'site',
      targetId: item.assetId ?? item.siteId,
    });
    events.push({
      id: `maintenance-end-${item.id}`,
      category: 'Maintenance',
      title: 'Maintenance window ended',
      detail: item.reason,
      occurredAt: item.endsAt,
      status: 'maintenance',
      siteId: item.siteId,
      assetId: item.assetId,
    });
  }
  for (const item of input.agents ?? []) {
    if (!item.lastHeartbeatAt) continue;
    events.push({
      id: `agent-${item.id}`,
      category: 'Agents',
      title: `Edge agent ${item.status}`,
      detail: `${customerFacingEdgeName(item)} · ${item.version}`,
      occurredAt: item.lastHeartbeatAt,
      status: item.status,
      target: 'edge',
      targetId: item.id,
    });
    if (item.appliedConfigVersion)
      events.push({
        id: `config-${item.id}-${item.appliedConfigVersion}`,
        category: 'Configuration',
        title: 'Edge configuration observed',
        detail: `${customerFacingEdgeName(item)} · version ${item.appliedConfigVersion}`,
        occurredAt: item.lastHeartbeatAt,
        status: item.configError ? 'warning' : 'informational',
        target: 'edge',
        targetId: item.id,
      });
  }
  for (const item of input.findings ?? []) {
    events.push({
      id: `capacity-${item.id}`,
      category: 'Capacity',
      title: item.title,
      detail: `${item.assetName} · ${customerMetricLabel(item.metric)}`,
      occurredAt: item.lastDetectedAt,
      status: item.severity,
      assetId: item.assetId,
      target: 'asset',
      targetId: item.assetId,
    });
  }
  return events
    .filter((item) => item.occurredAt)
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
}

export function filterOperationalEvents(
  events: OperationalEvent[],
  rangeDays: number,
  categories: TimelineCategory[],
  now = Date.now(),
) {
  const since = now - rangeDays * 86_400_000;
  const selected = new Set(categories);
  return events.filter(
    (event) => selected.has(event.category) && new Date(event.occurredAt).getTime() >= since,
  );
}

export type HealthFactor = { label: string; detail: string; status: string };

export function deriveHealthFactors(input: {
  incidents?: any[];
  findings?: any[];
  agents?: any[];
  assets?: any[];
  interfaces?: any[];
  health?: string;
}): HealthFactor[] {
  const factors: HealthFactor[] = [];
  const open = (input.incidents ?? []).filter((item) => item.status !== 'recovered');
  const critical = open.filter((item) => item.severity === 'critical').length;
  const warning = open.filter((item) => item.severity === 'warning').length;
  if (critical)
    factors.push({
      label: `${critical} critical incident${critical === 1 ? '' : 's'}`,
      detail: 'Existing incident state',
      status: 'critical',
    });
  if (warning)
    factors.push({
      label: `${warning} warning incident${warning === 1 ? '' : 's'}`,
      detail: 'Existing incident state',
      status: 'warning',
    });
  const findings = input.findings ?? [];
  if (findings.length)
    factors.push({
      label: `${findings.length} capacity finding${findings.length === 1 ? '' : 's'}`,
      detail: 'Current capacity analysis',
      status: findings.some((item) => item.severity === 'critical') ? 'critical' : 'warning',
    });
  const unhealthyAgents = (input.agents ?? []).filter((item) => item.status !== 'online');
  if (unhealthyAgents.length)
    factors.push({
      label: `${unhealthyAgents.length} Edge agent${unhealthyAgents.length === 1 ? '' : 's'} not online`,
      detail: 'Current Edge agent state',
      status: 'stale',
    });
  const unhealthyAssets = (input.assets ?? []).filter((item) =>
    ['critical', 'warning', 'unknown'].includes(item.health),
  );
  if (unhealthyAssets.length)
    factors.push({
      label: `${unhealthyAssets.length} asset${unhealthyAssets.length === 1 ? '' : 's'} require review`,
      detail: 'Reported asset health',
      status: unhealthyAssets.some((item) => item.health === 'critical') ? 'critical' : 'warning',
    });
  const interfaceIssues = (input.interfaces ?? []).filter(
    (item) => item.operStatus !== 'up' || Number(item.inErrors) + Number(item.outErrors) > 0,
  );
  if (interfaceIssues.length)
    factors.push({
      label: `${interfaceIssues.length} interface${interfaceIssues.length === 1 ? '' : 's'} with reported issues`,
      detail: 'Observed interface state/errors',
      status: 'warning',
    });
  if (!factors.length)
    factors.push({
      label:
        input.health === 'healthy'
          ? 'No contributing issues reported'
          : 'No detailed factors reported',
      detail: 'Additional contributing details are not available',
      status: input.health ?? 'informational',
    });
  return factors;
}

export function freshnessLabel(value?: string | null) {
  if (!value) return 'Freshness unavailable';
  const normalized = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value;
  const milliseconds = Date.now() - new Date(normalized).getTime();
  if (milliseconds < 60_000)
    return `Updated ${Math.max(0, Math.floor(milliseconds / 1000))} sec ago`;
  if (milliseconds < 3_600_000) return `Updated ${Math.floor(milliseconds / 60_000)} min ago`;
  return `Updated ${Math.floor(milliseconds / 3_600_000)} hr ago`;
}

export function similarIncidents(items: any[], selected: any, max = 5) {
  const type = selected.type;
  const correlation = selected.correlation_key ?? selected.correlationKey;
  return items
    .filter(
      (item) =>
        item.id !== selected.id &&
        (item.type === type ||
          (correlation && (item.correlationKey ?? item.correlation_key) === correlation)),
    )
    .sort(
      (a, b) =>
        new Date(b.updatedAt ?? b.updated_at).getTime() -
        new Date(a.updatedAt ?? a.updated_at).getTime(),
    )
    .slice(0, max);
}

export type ParsedRoute = {
  view: string;
  detailId?: string;
  topologyAssetId?: string;
  topologyServiceId?: string;
};

export function parsePortalRoute(pathname: string, search = ''): ParsedRoute {
  const parts = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  const params = new URLSearchParams(search);
  if (!parts.length || parts[0] === 'overview') return { view: 'Overview' };
  if (parts[0] === 'sites') return { view: 'Sites', detailId: parts[1] };
  if (parts[0] === 'edges') return { view: 'Edges', detailId: parts[1] };
  if (parts[0] === 'assets') return { view: 'Assets', detailId: parts[1] };
  if (parts[0] === 'logs') return { view: 'Logs' };
  if (parts[0] === 'integrations') return { view: 'Integrations' };
  if (parts[0] === 'maps') return { view: 'Maps' };
  if (parts[0] === 'topology') {
    const asset = params.get('asset');
    const service = params.get('service');
    return {
      view: 'Topology',
      ...(asset ? { topologyAssetId: asset } : {}),
      ...(service ? { topologyServiceId: service } : {}),
    };
  }
  if (parts[0] === 'incidents') return { view: 'Incidents', detailId: parts[1] };
  if (parts[0] === 'services') return { view: 'Services', detailId: parts[1] };
  if (parts[0] === 'capacity') return { view: 'Capacity' };
  if (parts[0] === 'reports') return { view: 'Reports' };
  if (parts[0] === 'opsai') return { view: 'OpsAI', detailId: params.get('incident') ?? undefined };
  if (parts[0] === 'knowledge') return { view: 'Knowledge' };
  if (parts[0] === 'settings') return { view: 'Settings' };
  if (parts[0] === 'aot' && parts[1] === 'customers')
    return { view: 'AOT Operations', detailId: parts[2] };
  if (parts[0] === 'aot' && parts[1] === 'onboarding')
    return { view: 'Customer Onboarding', detailId: parts[2] };
  if (parts[0] === 'aot' && parts[1] === 'wallboard') return { view: 'NOC Wallboard' };
  if (parts[0] === 'noc') return { view: 'NOC Wallboard' };
  return { view: 'Overview' };
}

const viewPaths: Record<string, string> = {
  Overview: '/overview',
  Sites: '/sites',
  Edges: '/edges',
  Assets: '/assets',
  Logs: '/logs',
  Integrations: '/integrations',
  Maps: '/maps',
  Topology: '/topology',
  Incidents: '/incidents',
  Services: '/services',
  Capacity: '/capacity',
  Reports: '/reports',
  OpsAI: '/opsai',
  Knowledge: '/knowledge',
  Settings: '/settings',
  'AOT Operations': '/aot/customers',
  'Customer Onboarding': '/aot/onboarding',
  'NOC Wallboard': '/noc',
};

export function portalPath(
  view: string,
  detailId?: string,
  topologyAssetId?: string,
  query: Record<string, string | number | boolean | undefined> = {},
) {
  const base = viewPaths[view] ?? '/overview';
  const params = new URLSearchParams(
    Object.entries(query)
      .filter(
        (entry): entry is [string, string | number | boolean] =>
          entry[1] !== undefined && entry[1] !== '',
      )
      .map(([key, value]) => [key, String(value)]),
  );
  if (
    detailId &&
    [
      'Sites',
      'Edges',
      'Assets',
      'Incidents',
      'Services',
      'AOT Operations',
      'Customer Onboarding',
    ].includes(view)
  )
    return `${base}/${encodeURIComponent(detailId)}${params.size ? `?${params}` : ''}`;
  if (view === 'OpsAI' && detailId) params.set('incident', detailId);
  if (view === 'Topology' && topologyAssetId) params.set('asset', topologyAssetId);
  return `${base}${params.size ? `?${params}` : ''}`;
}

export function safeSavedFilters(filters: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(filters).filter(([key, value]) => key !== 'search' && value),
  );
}

export function boundedInventoryPage(total: number, offset: number, pageSize = 100) {
  const limit = Math.max(1, Math.min(100, Math.trunc(pageSize) || 100));
  const safeTotal = Math.max(0, Math.trunc(total) || 0);
  const safeOffset = Math.min(Math.max(0, Math.trunc(offset) || 0), Math.max(0, safeTotal - 1));
  const pageOffset = Math.floor(safeOffset / limit) * limit;
  return {
    limit,
    offset: pageOffset,
    start: safeTotal ? pageOffset + 1 : 0,
    end: Math.min(safeTotal, pageOffset + limit),
    page: Math.floor(pageOffset / limit) + 1,
    pages: Math.max(1, Math.ceil(safeTotal / limit)),
  };
}

export function matchesIncidentFilter(
  incident: any,
  filters: { status?: string; severity?: string; site?: string; asset?: string; service?: string },
) {
  const statusMatches =
    !filters.status ||
    (filters.status === 'open'
      ? incident.status !== 'recovered'
      : incident.status === filters.status);
  return (
    statusMatches &&
    (!filters.severity || incident.severity === filters.severity) &&
    (!filters.site || incident.siteId === filters.site) &&
    (!filters.asset || incident.primaryAssetId === filters.asset) &&
    (!filters.service ||
      String(incident.affectedServiceIds ?? '')
        .split(',')
        .includes(filters.service))
  );
}

export function matchesServiceHealth(health: string | null | undefined, filter: string) {
  if (!filter) return true;
  if (filter === 'attention') return ['degraded', 'critical'].includes(String(health));
  return health === filter;
}
